const LOGIN_STATE_COLOR = {
  live: 'bg-green-100 text-green-700',
  fun_captcha: 'bg-yellow-100 text-yellow-700',
  recaptcha: 'bg-yellow-100 text-yellow-700',
  text_captcha: 'bg-yellow-100 text-yellow-700',
  unknown_captcha: 'bg-yellow-100 text-yellow-700',
  verify_code_via_email: 'bg-yellow-100 text-yellow-700',
  verify_code_2fa: 'bg-yellow-100 text-yellow-700',
  verify_other_device: 'bg-yellow-100 text-yellow-700',
  wrong_pass: 'bg-red-100 text-red-700',
  cp956: 'bg-red-100 text-red-700',
  cp282: 'bg-red-100 text-red-700',
  disabled: 'bg-red-100 text-red-700',
  invalid_request: 'bg-red-100 text-red-700',
  logged_out: 'bg-slate-100 text-slate-600',
  unknown: 'bg-slate-100 text-slate-600',
};

export function Badge({ className, children }) {
  return <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${className}`}>{children}</span>;
}

export function LoginStateBadge({ value }) {
  const colorClass = LOGIN_STATE_COLOR[value] || 'bg-slate-100 text-slate-600';
  return <Badge className={colorClass}>{value}</Badge>;
}

export function WorkingStateBadge({ value }) {
  const colorClass = value === 'working' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
  return <Badge className={colorClass}>{value}</Badge>;
}

export function LiveDot({ isLive, title }) {
  return (
    <span
      title={title}
      className={`inline-block h-2.5 w-2.5 rounded-full ${isLive ? 'bg-green-500' : 'bg-red-400'}`}
    />
  );
}

export function NeedReloginBadge({ value }) {
  if (!value) return null;
  return <Badge className="bg-orange-100 text-orange-700">cần relogin</Badge>;
}

export function getErrorMessage(error) {
  return (
    error.response?.data?.message ||
    error.response?.data?.detail ||
    error.message ||
    'Request failed'
  );
}

// Backend dùng const.INF_POS/INF_NEG (9876543210 / -9876543210) làm sentinel
// "không xác định" thay cho JSON Infinity thật - không phải số tiền thật,
// phải lọc trước khi format.
const INF_SENTINEL_ABS = 9876543210;

export function formatVnd(value) {
  if (value == null || !Number.isFinite(value) || Math.abs(value) >= INF_SENTINEL_ABS) return '—';
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}đ`;
}

export function formatNumber(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value >= INF_SENTINEL_ABS) return '+INF';
  if (value <= -INF_SENTINEL_ABS) return '-INF';
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// MySQL-style "YYYY-MM-DD HH:MM:SS" - CẮT CHUỖI THUẦN, không parse qua
// Date/local timezone. Backend trả ISO8601 UTC nguyên văn
// (datetime.isoformat(), vd "2026-10-05T07:50:17.640154"); hiển thị đúng
// y nguyên giá trị đó (chỉ đổi "T" thành space, bỏ phần sau giây) để
// copy-paste thẳng vào ô filter added_at_prefix luôn khớp - không bị lệch
// theo timezone trình duyệt. Chuỗi dạng này vẫn sort đúng tự nhiên theo
// thứ tự string (ISO8601 được thiết kế để sort-friendly).
export function formatDateTime(isoString) {
  if (!isoString) return '—';
  if (isoString.startsWith('0001-01-01') || isoString.startsWith('9999-12-31')) return '—';
  const [datePart, timePart] = isoString.split('T');
  if (!datePart || !timePart) return isoString;
  const seconds = timePart.slice(0, 8); // "HH:MM:SS", bỏ microsecond
  return `${datePart} ${seconds}`;
}

export function isSentinelMin(isoString) {
  return Boolean(isoString) && isoString.startsWith('0001-01-01');
}

// Backend's ConfigRegistry.all_values() stringify bool qua Python's
// str(bool) ("True"/"False" hoa chữ đầu) ở vài phiên bản cũ trước khi được
// sửa thành lowercase ("true"/"false") - so sánh case-insensitive để không
// phụ thuộc đúng backend đã deploy bản fix hay chưa.
export function isConfigTrue(value) {
  return String(value).toLowerCase() === 'true';
}

export const PROVIDER_STATE_COLOR = {
  live_nearly: '#2baf2b',
  live_need_healthcheck: '#a1ac2a',
  wait: '#28bbbb',
  die_wait_to_recover: '#ae6d23',
  die_in_recovering: '#2341b9',
  die_need_relogin: '#c92548',
  die_recover_fail: '#e07b39',
  die_permanently: '#b526bf',
};
export const PROVIDER_STATE_ORDER = Object.keys(PROVIDER_STATE_COLOR);

export const LOGIN_STATE_BAR_COLOR = {
  live: '#2baf2b',
  logged_out: '#8b5cf6',
  fun_captcha: '#eab308',
  recaptcha: '#0891b2',
  text_captcha: '#d946ef',
  unknown_captcha: '#f97316',
  verify_code_via_email: '#2563eb',
  verify_code_2fa: '#65a30d',
  verify_other_device: '#be123c',
  invalid_request: '#0d9488',
  wrong_pass: '#c92548',
  cp956: '#4338ca',
  cp282: '#ca8a04',
  disabled: '#9f1239',
  unknown: '#64748b',
};

// Bar dạng dọc + legend màu chữ bên phải - dùng chung ở OverviewTab,
// AccountsTab (working_state/login_state) và ProvidersTab (7-way state).
export function VerticalStackedBar({ segments, total, onClick, hideZero, barHeight = 'h-28' }) {
  const visible = hideZero ? segments.filter((s) => s.count > 0) : segments;
  return (
    <div className="flex items-stretch gap-3">
      <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        className={`flex ${barHeight} w-6 flex-col overflow-hidden rounded-full bg-slate-100 disabled:cursor-default`}
      >
        {segments.map(({ key, count, color }) => {
          if (!count) return null;
          const pct = total > 0 ? (count / total) * 100 : 0;
          return <div key={key} title={`${key}: ${count}`} style={{ height: `${pct}%`, backgroundColor: color }} />;
        })}
      </button>
      <div className="flex flex-col justify-center gap-0.5 text-[12px]">
        {visible.map((s) => (
          <span key={s.key} style={{ color: s.color }} className="font-bold">
            {s.key} : {s.count}
          </span>
        ))}
      </div>
    </div>
  );
}

const SERVICE_TYPE_SHORT = { fb_ql: 'QL', fb_eaaaau: 'EAAAAU', fb_eaagno: 'EAAGNO' };
const BRANCH_LABEL = { all: 'All', only: 'Only' };

// 6 cột (3 service_type x all/only) từ manager.get_providers_stats_from_list()'s
// by_service_type/each_service_type (dict lồng {all, only}, mỗi nhánh 1 bộ
// 7-state ProviderState). "all" = account chủ đang serve tất cả (chưa bị
// rebalance cô lập), "only" = account chủ đang cô lập CHỈ serve đúng
// service_type này. Tách riêng branch này ở MỌI state (không chỉ live_*) vì
// 1 account đang hồi phục (die_wait_to_recover/die_in_recovering/
// die_need_relogin) có thể đã bị rebalance cô lập sang service khác - hồi phục
// xong vẫn không đóng góp vào pool serving thực tế của service khác, nên
// không nên gộp chung với "all" rồi tưởng nhầm là sắp có thêm capacity.
// Cột label 8 state dùng chung (KHÔNG lặp lại trong mỗi bar) - per user's
// explicit decision 2026-10-31: 6 bar cạnh nhau mà bar nào cũng tự in tên 8
// state rất tốn diện tích ngang, trong khi cả 6 bar luôn cùng 1 bộ
// PROVIDER_STATE_ORDER cố định nên 1 cột legend chung là đủ.
function ProviderStateLegendColumn() {
  return (
    <div>
      {/* 2 dòng placeholder rỗng khớp đúng chiều cao header 2 dòng của mỗi
          group (service_type label + All/Only label) ở ProviderStatsByServing. */}
      <p className="mb-1 text-xs font-medium text-slate-600">&nbsp;</p>
      <p className="mb-1 text-center text-[12px] font-medium text-slate-500">&nbsp;</p>
      <div className="flex h-[10.5rem] flex-col justify-between py-0.5 text-[12px]">
        {PROVIDER_STATE_ORDER.map((state) => (
          <span key={state} style={{ color: PROVIDER_STATE_COLOR[state] }} className="font-bold">
            {state}
          </span>
        ))}
      </div>
    </div>
  );
}

function ProviderStatsBranchBar({ serviceType, branch, buckets, total, onSegmentClick, showBars }) {
  const segments = PROVIDER_STATE_ORDER.map((state) => ({
    key: state,
    count: buckets[state]?.count || 0,
    suspended: buckets[state]?.suspended || 0,
    color: PROVIDER_STATE_COLOR[state],
  }));
  return (
    <div>
      <p className="mb-1 text-left text-[12px] font-medium text-slate-500">
        {BRANCH_LABEL[branch]} ({total})
      </p>
      <div className="flex items-stretch gap-1.5">
        {showBars && (
          <button
            type="button"
            onClick={() => onSegmentClick && onSegmentClick(serviceType, branch)}
            disabled={!onSegmentClick}
            className="flex h-[10.5rem] w-2 flex-col overflow-hidden bg-slate-100 disabled:cursor-default"
          >
            {segments.map(({ key, count, color }) => {
              if (!count) return null;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return <div key={key} title={`${key}: ${count}`} style={{ height: `${pct}%`, backgroundColor: color }} />;
            })}
          </button>
        )}
        <div
          className={`flex flex-col justify-between py-0.5 text-[12px] ${showBars ? '' : 'h-[10.5rem] cursor-pointer'}`}
          onClick={!showBars ? () => onSegmentClick && onSegmentClick(serviceType, branch) : undefined}
        >
          {segments.map((s) => (
            <span key={s.key} style={{ color: s.color }} className="font-bold">
              {s.count}
              {s.suspended > 0 && <span className="text-slate-400"> ({s.suspended})</span>}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Mỗi service_type là 1 cặp cột All/Only luôn đi liền nhau (per user's explicit
// decision 2026-10-31: gom cặp vào 1 group không flex-wrap riêng, tránh khi
// thu hẹp màn hình All của 1 service lại wrap tách rời khỏi Only của chính
// nó, hoặc header service_type bị lặp lại 2 lần).
export function ProviderStatsByServing({ providersStats, onSegmentClick, showBars = true }) {
  if (!providersStats) return null;
  return (
    <div className="flex flex-wrap items-start gap-6">
      <ProviderStateLegendColumn />
      {Object.entries(providersStats.by_service_type).map(([serviceType, branches]) => (
        <div key={serviceType}>
          <p className="mb-1 text-center text-xs font-semibold text-slate-700">{SERVICE_TYPE_SHORT[serviceType] || serviceType}</p>
          <div className="flex items-start gap-3">
            {['all', 'only'].map((branch) => (
              <ProviderStatsBranchBar
                key={branch}
                serviceType={serviceType}
                branch={branch}
                buckets={branches[branch]}
                total={providersStats.each_service_type[serviceType][branch]}
                onSegmentClick={onSegmentClick}
                showBars={showBars}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
